import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  constructor() {
    super();
    // OTIMIZAÇÃO: Garante que o 'this' seja sempre a classe, não o elemento DOM
    this.suspend = this.suspend.bind(this);
    this.resume = this.resume.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.play();
  }

  // OTIMIZAÇÃO CRÍTICA: Limpeza de memória ao remover o componente
  disconnectedCallback() {
    super.disconnectedCallback?.(); // Chama o super se existir
    this.suspend(); // Para o timer
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    
    // OTIMIZAÇÃO: Limpa intervalo anterior se existir para evitar "duplo play"
    if (this.#interval) clearInterval(this.#interval);

    this.#interval = setInterval(() => {
      // OTIMIZAÇÃO: Removemos a checagem de :hover e document.hidden aqui
      // pois os EventListeners já cuidam de pausar o timer.
      // Isso deixa o loop mais leve.
      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = undefined;
    }
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    // Só retoma se autoplay estiver ativo E não tiver sido pausado manualmente
    if (!this.autoplay || this.paused) return;

    // Não chamamos this.pause() aqui para não setar o atributo 'paused'
    // Apenas garantimos que o timer anterior parou e iniciamos um novo
    this.suspend();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;

    let relativeIndex = current % slides.length;
    if (relativeIndex < 0) {
      relativeIndex += slides.length;
    }

    // Mantendo a lógica original do aria-hidden para compatibilidade CSS
    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  handleVisibilityChange() {
    if (document.hidden) {
      this.suspend();
    } else {
      this.resume();
    }
  }
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}